import type { Bookmark } from "@repo/fika-shared/src/loader/bookmark-schema.js"
import type { Publisher } from "@repo/fika-shared/src/loader/publisher-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import { sortBy } from "remeda"
import { createMemo, Show } from "solid-js"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { Info } from "@/designSystem/patterns/info"
import { List } from "@/designSystem/patterns/list"
import { Navigation } from "@/designSystem/patterns/navigation"
import { StoryComponent } from "@/designSystem/patterns/story"
import {
  TagSuggestions,
  TagWithSuggestions,
} from "@/designSystem/patterns/tags-with-suggestions"
import { Title } from "@/designSystem/patterns/title"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/getTitle"
import { createPublishersRouter } from "@/routes/publishers-router"
import { hrClass, pillClass } from "./publisher-item.css"
import usePublisher from "./use-publisher"

export function PublisherItem() {
  const getPublisher = usePublisher()

  return (
    <Show when={getPublisher()}>
      {publisher => <SafePublisherItem publisher={publisher()} />}
    </Show>
  )
}

function SafePublisherItem(props: { publisher: Publisher }) {
  const { LL } = useI18nContext()
  const { db, replicache } = useAppContext()
  const title = createMemo(() => getTitle(props.publisher.dc_title))
  const { paths } = createPublishersRouter()

  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.feeds(),
        href: paths().url,
      } as const,
    ]
  })

  const rootUrl = createMemo(() => {
    try {
      const parsedUrl = new URL(props.publisher.url)
      return parsedUrl.origin
    } catch (error) {
      console.error("Invalid URL provided:", error)
      return null
    }
  })

  const getSubscription = createMemo(
    () => db.subscription.findBy({ publisher_id: props.publisher.id }) || null,
  )

  return (
    <MainLayout
      header={<Navigation hue="indigo" title={title()} buttons={buttons()} />}
      footer={
        <ButtonDrawer
          hue="indigo"
          leftActions={
            <Show when={rootUrl()}>
              {url => (
                <LinkButton
                  hue="indigo"
                  kind="darkGhost"
                  iconName="external-link"
                  target="_blank"
                  href={url()}
                >
                  visit
                </LinkButton>
              )}
            </Show>
          }
          rightActions={
            <>
              <Show when={!getSubscription()}>
                <Button
                  hue="indigo"
                  iconName="bell-off"
                  kind="darkGhost"
                  onClick={() => {
                    replicache.mutate.setSubscription({
                      id: ulid(),
                      publisher_id: props.publisher.id,
                      silenced: true,
                      created_at: new Date().toISOString(),
                    })
                  }}
                >
                  ignore
                </Button>
              </Show>
              <Show
                when={getSubscription() && !getSubscription()?.silenced}
                fallback={
                  <Button
                    hue="indigo"
                    iconName="bell-plus"
                    onClick={() => {
                      replicache.mutate.setSubscription({
                        id: getSubscription()?.id || ulid(),
                        publisher_id: props.publisher.id,
                        silenced: false,
                        created_at: new Date().toISOString(),
                      })
                    }}
                  >
                    subscribe
                  </Button>
                }
              >
                <Button
                  iconName="bell-minus"
                  hue="indigo"
                  onClick={() => {
                    replicache.mutate.deleteSubscription(getSubscription()!.id)
                  }}
                >
                  unsubscribe
                </Button>
              </Show>
            </>
          }
        />
      }
    >
      <PublisherDetail {...props} />
    </MainLayout>
  )
}

type Result = {
  story: Story
  bookmark: Bookmark | null
}

type PublisherDetailProps = {
  publisher: Publisher
}
function PublisherDetail(props: PublisherDetailProps) {
  const { db } = useAppContext()
  const { paths } = createPublishersRouter()

  const getSubscription = createMemo(
    () => db.subscription.findBy({ publisher_id: props.publisher.id }) || null,
  )

  const title = createMemo(() => getTitle(props.publisher.dc_title))
  const results = createMemo(() =>
    db.entry
      .filterBy({ publisher_id: props.publisher.id })
      .map(entry => {
        const story = db.story.get(entry.story_id)
        if (!story) return null

        const bookmark = db.bookmark.findBy({ story_id: story.id })

        return { story, bookmark, entry }
      })
      .filter(r => !!r),
  )

  const groupedResults = createMemo(() => {
    const bookmarked: Result[] = []
    const other: Result[] = []
    const sorted = sortBy(results(), [
      result =>
        result.bookmark?.bookmarked_at ||
        result.entry.published_at ||
        result.story.dc_created_at ||
        0,
      "desc",
    ])

    for (const result of sorted) {
      if (result.bookmark) {
        bookmarked.push(result)
      } else {
        other.push(result)
      }
    }

    return { bookmarked, other }
  })

  const tagOptions = createMemo(() => {
    const target = getSubscription()
    if (!target) return []
    return db.tag.filterBy({ target_type: "subscription" }).map(t => t.tag)
  })

  const getTags = createMemo(() => {
    const target = getSubscription()
    if (!target) return new Set([] as string[])

    return new Set(
      db.tag
        .filterBy({ target_id: target.id, target_type: "subscription" })
        .map(t => t.tag),
    )
  })

  return (
    <Box gap="12" fill>
      <Title
        title={title()}
        hue="indigo"
        iconName="newspaper"
        subtitle={
          <Show when={props.publisher.dc_description}>
            {description => (
              <Text
                fontSize="m"
                color="sand800"
                lineClamp="3"
                textAlign="center"
              >
                {description()}
              </Text>
            )}
          </Show>
        }
      />
      <Show
        when={getSubscription()}
        fallback={<TagSuggestions suggestions={props.publisher.tags} />}
      >
        {subscription => (
          <TagWithSuggestions
            options={tagOptions()}
            tags={getTags()}
            suggestions={props.publisher.tags}
            targetType="subscription"
            targetId={subscription().id}
            hue="indigo"
          />
        )}
      </Show>
      <Show when={getSubscription()?.silenced}>
        <Box
          padding={{ inline: "12", block: "8" }}
          background="sand100"
          gap="16"
          flexDirection="row"
          alignItems="center"
        >
          <Icon name="bell-off" size="24" color="sand800" />
          <Box>
            <Text color="sand800" fontSize="m" fontWeight="500">
              ignored
            </Text>
            <Text color="sand700" fontSize="s">
              we won't recommend this feed for you to subscribe anymore.
            </Text>
          </Box>
        </Box>
      </Show>
      <Box fill>
        <Show when={groupedResults().bookmarked.length}>
          <Box>
            <Section title="bookmarked" />
            <List
              list={groupedResults().bookmarked}
              content={result => <StoryComponent story={result.story} />}
              href={result =>
                paths().publisher(props.publisher).story(result.story).url
              }
            />
          </Box>
        </Show>
        <Box>
          <Section title="recent" />
          <Show
            when={groupedResults().other.length}
            fallback={
              <Info iconName="rabbit">syncing stories to your device</Info>
            }
          >
            <List
              list={groupedResults().other}
              content={result => <StoryComponent story={result.story} />}
              href={result =>
                paths().publisher(props.publisher).story(result.story).url
              }
            />
          </Show>
        </Box>
      </Box>
    </Box>
  )
}

function Section(props: { title: string }) {
  return (
    <Box
      padding={{ block: "16" }}
      flexDirection="row"
      alignItems="center"
      gap="12"
      justifyContent="space-between"
    >
      <hr class={hrClass} />
      <Box padding={{ inline: "8" }} borderRadius="24" class={pillClass}>
        <Text fontSize="s" color="sand600">
          {props.title}
        </Text>
      </Box>
      <hr class={hrClass} />
    </Box>
  )
}
